import { useEffect, useState } from 'react'
import {AlertTriangle, Eye, FileText, Paperclip, Upload, Users, X} from 'lucide-react'
import toast from 'react-hot-toast'
import { lumi } from '@/lib/lumi'
import { useAppAuth } from '@/contexts/AuthContext'
import { generateProtocolNumber, maskCPF, nowISO, unmaskCPF } from '@/utils/masks'
import { MOTIVOS } from '@/components/protocolos/StatusBadge'
import { abrirJanelaDocumento, gerarHtmlRequerimento } from '@/utils/documentoRequerimento'
import DatasAbonadasField from '@/components/protocolos/DatasAbonadasField'
import { ANEXO_OBRIGATORIO, datasDoProtocolo, datasParaIso } from '@/components/protocolos/requerimentoConfig'
import { gerarChaveAssinatura, montarAssinatura } from '@/utils/chaveAssinatura'

interface NovoProtocoloModalProps {
  onClose: () => void
  onCreated: () => void
  /** Se fornecido, o modal funciona em modo de edição. */
  protocolo?: any
}

function NovoProtocoloModal({ onClose, onCreated, protocolo }: NovoProtocoloModalProps) {
  const { user } = useAppAuth()
  const editando = !!protocolo
  const isAdmin = user?.role === 'ADMIN'
  const isCoordenador = user?.role === 'COORDENADOR'
  const temSecretariaFixa = !!user?.secretariaId
  const [cidades, setCidades] = useState<any[]>([])
  const [secretarias, setSecretarias] = useState<any[]>([])
  const [sublocais, setSublocais] = useState<any[]>([])
  const [cidadeId, setCidadeId] = useState(protocolo?.cidadeId || user?.cidadeId || '')
  const [secretariaId, setSecretariaId] = useState(protocolo?.secretariaId || user?.secretariaId || '')
  const [subLocalId, setSubLocalId] = useState(protocolo?.subLocalId || '')
  const [nomeCompleto, setNomeCompleto] = useState(protocolo?.nomeCompleto || '')
  const [cpf, setCpf] = useState(protocolo?.cpf ? maskCPF(protocolo.cpf) : '')
  const [funcaoExercida, setFuncaoExercida] = useState(protocolo?.funcaoExercida || '')
  const [coletivo, setColetivo] = useState<boolean>(!!protocolo?.coletivo)
  const [localTrabalho, setLocalTrabalho] = useState(protocolo?.localTrabalho || '')
  const [datas, setDatas] = useState<string[]>(() => {
    const existentes = protocolo ? datasDoProtocolo(protocolo) : []
    return existentes.length ? existentes : ['']
  })
  const [motivo, setMotivo] = useState(protocolo?.motivo || '')
  const [descricaoProblema, setDescricaoProblema] = useState(protocolo?.descricaoProblema || '')
  const [descricaoOutro, setDescricaoOutro] = useState(protocolo?.descricaoOutro || '')
  const [anexosExistentes, setAnexosExistentes] = useState<any[]>(protocolo?.anexos || [])
  const [atestadoFile, setAtestadoFile] = useState<File | null>(null)
  const [atestadoUploading, setAtestadoUploading] = useState(false)
  const [anexosFiles, setAnexosFiles] = useState<File[]>([])
  const [anexosUploading, setAnexosUploading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [protocoloEnviado, setProtocoloEnviado] = useState<{ numeroProtocolo: string, dadosDocumento: any } | null>(null)
  const [etapaEnvio, setEtapaEnvio] = useState<'aviso' | 'imprimir'>('aviso')
  const [erroAnexo, setErroAnexo] = useState('')

  const anexoObrigatorio = ANEXO_OBRIGATORIO[motivo]

  useEffect(() => {
    (lumi.entities as any).cidades.list({ sort: { nome: 1 }, limit: 200 })
      .then((r: any) => setCidades(r?.list || []))
      .catch(() => setCidades([]))
  }, [])

  useEffect(() => {
    const cidadeAlvo = isAdmin ? cidadeId : (user?.cidadeId || '')
    if (!cidadeAlvo) {
      setSecretarias([])
      return
    }
    (lumi.entities as any).secretarias.list({ filter: { cidadeId: cidadeAlvo }, sort: { nome: 1 }, limit: 200 })
      .then((r: any) => setSecretarias(r?.list || []))
      .catch(() => setSecretarias([]))
  }, [cidadeId, isAdmin])

  useEffect(() => {
    const secretariaAlvo = temSecretariaFixa ? (user?.secretariaId || '') : secretariaId
    if (!secretariaAlvo) {
      setSublocais([])
      return
    }
    (lumi.entities as any).sublocais.list({ filter: { secretariaId: secretariaAlvo }, sort: { nome: 1 }, limit: 200 })
      .then((r: any) => {
        const lista = r?.list || []
        if (isCoordenador) {
          setSublocais(lista.filter((s: any) => user?.subLocalIds?.includes(s._id)))
        }
        else {
          setSublocais(lista)
        }
      })
      .catch(() => setSublocais([]))
  }, [secretariaId, temSecretariaFixa, isCoordenador])

  useEffect(() => {
    if (!editando && isCoordenador && user?.subLocalIds?.length === 1)
      setSubLocalId(user.subLocalIds[0])
  }, [isCoordenador, user])

  const cidadeNomeAtual = cidades.find(c => c._id === user?.cidadeId)?.nome
  const secretariaNomeAtual = secretarias.find(s => s._id === user?.secretariaId)?.nome

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const datasIso = datasParaIso(datas)
    if (coletivo && !localTrabalho) {
      toast.error('Informe o local de trabalho do requerimento coletivo.')
      return
    }
    if (!coletivo && (!nomeCompleto || !cpf || !funcaoExercida)) {
      toast.error('Preencha nome, CPF e função do requerente.')
      return
    }
    if (!localTrabalho || datasIso.length === 0 || !motivo) {
      toast.error('Preencha todos os campos obrigatórios, incluindo pelo menos uma data.')
      return
    }
    if (isAdmin && !cidadeId) {
      toast.error('Selecione a cidade do requerimento.')
      return
    }
    if (!secretariaId) {
      toast.error('Selecione a secretaria de trabalho.')
      return
    }
    if (isCoordenador && !subLocalId) {
      toast.error('Selecione o sub local do requerimento.')
      return
    }
    if (motivo === 'Problema na Entrada/Saída' && !descricaoProblema) {
      toast.error('Descreva o problema.')
      return
    }
    if (motivo === 'Outro' && !descricaoOutro) {
      toast.error('Descreva o motivo.')
      return
    }
    if (anexoObrigatorio && !atestadoFile && !(editando && protocolo?.atestadoUrl)) {
      setErroAnexo(anexoObrigatorio.erro)
      toast.error(anexoObrigatorio.erro)
      return
    }
    setErroAnexo('')

    setSaving(true)
    try {
      let atestadoUrl = ''
      let atestadoNomeArquivo = ''
      if (anexoObrigatorio && atestadoFile) {
        setAtestadoUploading(true)
        const uploadResults = await lumi.tools.file.upload([atestadoFile])
        setAtestadoUploading(false)
        const resultado = uploadResults?.[0]
        if (!resultado || resultado.uploadError || !resultado.fileUrl) {
          toast.error(resultado?.uploadError || 'Erro ao enviar o atestado. Tente novamente.')
          setSaving(false)
          return
        }
        atestadoUrl = resultado.fileUrl
        atestadoNomeArquivo = resultado.fileName || atestadoFile.name
      }

      let anexos: any[] = []
      if (anexosFiles.length > 0) {
        setAnexosUploading(true)
        const uploadResults = await lumi.tools.file.upload(anexosFiles)
        setAnexosUploading(false)
        const comErro = uploadResults?.find((r: any) => r.uploadError || !r.fileUrl)
        if (comErro) {
          toast.error(comErro.uploadError || 'Erro ao enviar um dos anexos. Tente novamente.')
          setSaving(false)
          return
        }
        anexos = (uploadResults || []).map((r: any, idx: number) => ({
          url: r.fileUrl,
          nomeArquivo: r.fileName || anexosFiles[idx]?.name || 'arquivo',
          tipo: (anexosFiles[idx]?.name || '').split('.').pop() || '',
          tamanho: anexosFiles[idx]?.size || 0,
          enviadoPorUserId: user?.userId || '',
          enviadoEm: nowISO(),
        }))
      }

      if (editando && protocolo) {
        const agora = nowISO()
        await (lumi.entities as any).protocolos.update(protocolo._id, {
          nomeCompleto: coletivo ? `Coletivo: ${localTrabalho}` : nomeCompleto,
          cpf: coletivo ? '' : unmaskCPF(cpf),
          funcaoExercida: coletivo ? '' : funcaoExercida,
          coletivo,
          localTrabalho,
          dataAbonada: datasIso[0],
          datasAbonadas: datasIso,
          motivo,
          descricaoProblema: motivo === 'Problema na Entrada/Saída' ? descricaoProblema : '',
          descricaoOutro: motivo === 'Outro' ? descricaoOutro : '',
          atestadoUrl: anexoObrigatorio ? (atestadoUrl || protocolo.atestadoUrl || '') : '',
          atestadoNomeArquivo: anexoObrigatorio ? (atestadoNomeArquivo || protocolo.atestadoNomeArquivo || '') : '',
          anexos: [...anexosExistentes, ...anexos],
          cidadeId: isAdmin ? cidadeId : (protocolo.cidadeId || user?.cidadeId || ''),
          secretariaId,
          subLocalId: subLocalId || '',
          editadoPorNome: user?.nomeCompleto || '',
          editadoEm: agora,
          updatedAt: agora,
        })

        await (lumi.entities as any).protocolo_historico.create({
          protocoloId: protocolo.numeroProtocolo,
          acao: 'EDITADO',
          descricao: `Requerimento editado por ${user?.nomeCompleto || 'utilizador'}.`,
          userId: user?.userId || 'desconhecido',
          creator: user?.userId || 'desconhecido',
          createdAt: agora,
        })

        toast.success('Requerimento atualizado com sucesso.')
        onCreated()
        onClose()
        return
      }

      const numeroProtocolo = generateProtocolNumber()
      const dataCriacao = nowISO()

      // Chave única de assinatura do autorizador (quem registou o requerimento)
      let chave = (user as any)?.chaveAssinatura
      if (!chave && user?.userId) {
        chave = await gerarChaveAssinatura(user.userId, user.cpf || '')
        try {
          await (lumi.entities as any).user_profiles.update(user._id, { chaveAssinatura: chave })
        }
        catch {
          /* não bloqueia o envio */
        }
      }
      const assinaturaAutorizador = await montarAssinatura({
        userId: user?.userId,
        nomeCompleto: user?.nomeCompleto,
        funcao: user?.funcao,
        cpf: user?.cpf,
        chaveAssinatura: chave,
      })

      await (lumi.entities as any).protocolos.create({
        numeroProtocolo,
        nomeCompleto: coletivo ? `Coletivo: ${localTrabalho}` : nomeCompleto,
        cpf: coletivo ? '' : unmaskCPF(cpf),
        funcaoExercida: coletivo ? '' : funcaoExercida,
        coletivo,
        localTrabalho,
        dataAbonada: datasIso[0],
        datasAbonadas: datasIso,
        motivo,
        descricaoProblema: motivo === 'Problema na Entrada/Saída' ? descricaoProblema : '',
        descricaoOutro: motivo === 'Outro' ? descricaoOutro : '',
        atestadoUrl,
        atestadoNomeArquivo,
        anexos,
        cidadeId: isAdmin ? cidadeId : (user?.cidadeId || ''),
        secretariaId,
        subLocalId: subLocalId || '',
        requerenteUserId: user?.userId || '',
        assinaturaAutorizador,
        status: 'AGUARDANDO_ANALISE',
        creator: user?.userId || 'desconhecido',
        createdAt: dataCriacao,
        updatedAt: dataCriacao,
      })

      await (lumi.entities as any).protocolo_historico.create({
        protocoloId: numeroProtocolo,
        acao: 'CRIADO',
        descricao: 'Protocolo criado e enviado para análise.',
        userId: user?.userId || 'desconhecido',
        creator: user?.userId || 'desconhecido',
        createdAt: nowISO(),
      })

      try {
        await (lumi.entities as any).notificacoes.create({
          tipo: 'NOVO_PROTOCOLO',
          mensagem: `Novo requerimento ${numeroProtocolo}${coletivo ? ` (coletivo — ${localTrabalho})` : ` de ${nomeCompleto}`} aguardando análise.`,
          numeroProtocolo,
          cidadeId: isAdmin ? cidadeId : (user?.cidadeId || ''),
          secretariaId,
          lidoPorUserIds: [],
          creator: user?.userId || 'desconhecido',
          createdAt: nowISO(),
          updatedAt: nowISO(),
        })
      }
      catch {
        /* não bloqueia o envio do protocolo caso a notificação falhe */
      }

      toast.success(`Protocolo ${numeroProtocolo} enviado com sucesso.`)
      onCreated()
      setEtapaEnvio('aviso')
      setProtocoloEnviado({
        numeroProtocolo,
        dadosDocumento: {
          numeroProtocolo,
          nomeCompleto: coletivo ? `Coletivo: ${localTrabalho}` : nomeCompleto,
          cpf: coletivo ? '' : unmaskCPF(cpf),
          funcaoExercida: coletivo ? '' : funcaoExercida,
          coletivo,
          localTrabalho,
          dataAbonada: datasIso[0],
          datasAbonadas: datasIso,
          motivo,
          descricaoProblema: motivo === 'Problema na Entrada/Saída' ? descricaoProblema : '',
          descricaoOutro: motivo === 'Outro' ? descricaoOutro : '',
          status: 'AGUARDANDO_ANALISE',
          parecer: '',
          createdAt: dataCriacao,
          assinaturaAutorizador,
        },
      })
    }
    catch {
      toast.error('Erro ao enviar protocolo. Tente novamente.')
    }
    finally {
      setSaving(false)
      setAtestadoUploading(false)
      setAnexosUploading(false)
    }
  }

  const handleImprimir = () => {
    if (!protocoloEnviado) return
    const cidadeAtual = cidades.find(c => c._id === (isAdmin ? cidadeId : user?.cidadeId))
    const html = gerarHtmlRequerimento(protocoloEnviado.dadosDocumento, {
      logoUrl: cidadeAtual?.logoUrl || '',
      nomeSistema: cidadeAtual?.nomeSistema || 'PROTOCOLEAI',
      cidadeNome: cidadeAtual?.cidadeNome || cidadeAtual?.nome || '',
      nome: cidadeAtual?.nome || '',
    })
    abrirJanelaDocumento(html)
    onClose()
  }

  const removerAnexo = (idx: number) => {
    setAnexosFiles(prev => prev.filter((_, i) => i !== idx))
  }

  if (protocoloEnviado) {
    if (etapaEnvio === 'aviso') {
      return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-[460px] p-6 text-center">
            <div className="w-14 h-14 rounded-full bg-[#FEF9E7] flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-7 h-7 text-[#B7791F]" strokeWidth={1.9} />
            </div>
            <h2 className="text-[17px] font-semibold text-[#12141A]">Atenção!</h2>
            <p className="text-[13.5px] text-[#4B5563] mt-2 leading-relaxed">
              O documento passará por avaliação e poderá ser rejeitado, fique acompanhando o processo.
            </p>
            <p className="text-[12px] text-[#9CA3AF] mt-3">
              Requerimento
              {' '}
              <span className="font-semibold text-[#12141A]">{protocoloEnviado.numeroProtocolo}</span>
              {' '}
              registado com sucesso.
            </p>
            <button
              type="button"
              onClick={() => setEtapaEnvio('imprimir')}
              className="mt-5 w-full px-4 py-2.5 rounded-lg text-[13.5px] font-medium bg-[#1E4FBF] hover:bg-[#1A44A6] text-white"
            >
              Entendi
            </button>
          </div>
        </div>
      )
    }

    return (
      <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl w-full max-w-[440px] p-6">
          <div className="w-12 h-12 rounded-full bg-[#EEF3FE] flex items-center justify-center mb-4">
            <FileText className="w-6 h-6 text-[#1E4FBF]" strokeWidth={1.75} />
          </div>
          <h2 className="text-[16px] font-semibold text-[#12141A] mb-1.5">
            Requerimento {protocoloEnviado.numeroProtocolo} enviado
          </h2>
          <p className="text-[13.5px] text-[#4B5563] mb-5">
            O seu requerimento foi enviado com sucesso e está aguardando análise. Deseja imprimir agora, para que o requerente possa assinar, se necessário?
          </p>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-lg text-[13.5px] font-medium text-[#4B5563] hover:bg-[#F5F6F8]"
            >
              Agora não
            </button>
            <button
              type="button"
              onClick={handleImprimir}
              className="px-4 py-2.5 rounded-lg text-[13.5px] font-medium bg-[#1E4FBF] hover:bg-[#1A44A6] text-white"
            >
              Imprimir
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-[640px] max-h-[92vh] sm:max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-[#E7E9EC] sticky top-0 bg-white z-10">
          <h2 className="text-[16px] font-semibold text-[#12141A]">{editando ? `Editar Requerimento ${protocolo.numeroProtocolo}` : 'Novo Requerimento'}</h2>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#12141A]">
            <X className="w-5 h-5" strokeWidth={1.75} />
          </button>
        </div>

        <form onSubmit={handleSubmit} autoComplete="off" className="p-4 sm:p-6 pb-8 sm:pb-6 flex flex-col gap-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[13px] font-medium text-[#374151] mb-1.5">Cidade</label>
              {isAdmin ? (
                <select
                  value={cidadeId}
                  onChange={(e) => {
                    setCidadeId(e.target.value)
                    setSecretariaId('')
                  }}
                  className="w-full px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] bg-white focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF]"
                >
                  <option value="">Selecione...</option>
                  {cidades.map(c => <option key={c._id} value={c._id}>{c.nome}</option>)}
                </select>
              ) : (
                <input value={cidadeNomeAtual || 'Cidade do utilizador'} disabled className="w-full px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] bg-[#F7F8FA] text-[#6B7280]" />
              )}
            </div>
            <div>
              <label className="block text-[13px] font-medium text-[#374151] mb-1.5">Secretaria de Trabalho</label>
              {temSecretariaFixa ? (
                <input value={secretariaNomeAtual || 'Secretaria do utilizador'} disabled className="w-full px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] bg-[#F7F8FA] text-[#6B7280]" />
              ) : (
                <select
                  value={secretariaId}
                  onChange={(e) => { setSecretariaId(e.target.value); setSubLocalId('') }}
                  disabled={isAdmin && !cidadeId}
                  className="w-full px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] bg-white focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF] disabled:bg-[#F7F8FA] disabled:text-[#6B7280]"
                >
                  <option value="">Selecione...</option>
                  {secretarias.map(s => <option key={s._id} value={s._id}>{s.nome}</option>)}
                </select>
              )}
            </div>
          </div>

          {sublocais.length > 0 && (
            <div>
              <label className="block text-[13px] font-medium text-[#374151] mb-1.5">
                Sub Local{isCoordenador ? '' : ' (opcional)'}
              </label>
              <select
                value={subLocalId}
                onChange={e => setSubLocalId(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] bg-white focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF]"
              >
                <option value="">{isCoordenador ? 'Selecione...' : 'Nenhum'}</option>
                {sublocais.map(s => <option key={s._id} value={s._id}>{s.nome}</option>)}
              </select>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className={`flex items-start gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-colors ${coletivo ? 'border-[#1E4FBF] bg-[#F2F6FE]' : 'border-[#E1E4E8] bg-white hover:bg-[#F7F8FA]'}`}>
                <input
                  type="checkbox"
                  checked={coletivo}
                  onChange={(e) => setColetivo(e.target.checked)}
                  className="accent-[#1E4FBF] w-4 h-4 mt-0.5"
                />
                <span>
                  <span className="flex items-center gap-1.5 text-[13.5px] font-semibold text-[#12141A]">
                    <Users className="w-4 h-4 text-[#1E4FBF]" strokeWidth={1.9} />
                    Requerimento coletivo
                  </span>
                  <span className="block text-[12px] text-[#6B7280] mt-0.5 leading-snug">
                    Para pedidos que abrangem vários servidores. Nome, CPF e função não são pedidos — basta indicar o local de trabalho.
                  </span>
                </span>
              </label>
            </div>

            {!coletivo && (
              <>
                <div>
                  <label className="block text-[13px] font-medium text-[#374151] mb-1.5">Nome Completo</label>
                  <input autoComplete="off" name="req-nome" value={nomeCompleto} onChange={e => setNomeCompleto(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF]" />
                </div>
                <div>
                  <label className="block text-[13px] font-medium text-[#374151] mb-1.5">CPF</label>
                  <input autoComplete="off" name="req-cpf" value={cpf} onChange={e => setCpf(maskCPF(e.target.value))} maxLength={14} className="w-full px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF]" />
                </div>
                <div>
                  <label className="block text-[13px] font-medium text-[#374151] mb-1.5">Função Exercida</label>
                  <input autoComplete="off" name="req-funcao" value={funcaoExercida} onChange={e => setFuncaoExercida(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF]" />
                </div>
              </>
            )}
            <div>
              <label className="block text-[13px] font-medium text-[#374151] mb-1.5">Local de Trabalho</label>
              <input autoComplete="off" name="req-local" value={localTrabalho} onChange={e => setLocalTrabalho(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF]" />
            </div>
            <DatasAbonadasField datas={datas} onChange={setDatas} />
          </div>

          <div>
            <label className="block text-[13px] font-medium text-[#374151] mb-2">Motivo</label>
            <div className="flex flex-col gap-2">
              {MOTIVOS.map(m => (
                <label key={m} className="flex items-center gap-2.5 text-[13.5px] text-[#12141A] cursor-pointer">
                  <input
                    type="radio"
                    name="motivo"
                    checked={motivo === m}
                    onChange={() => {
                      setMotivo(m)
                      setAtestadoFile(null)
                      setErroAnexo('')
                    }}
                    className="accent-[#1E4FBF] w-4 h-4"
                  />
                  {m}
                </label>
              ))}
            </div>
          </div>

          {motivo === 'Problema na Entrada/Saída' && (
            <div>
              <label className="block text-[13px] font-medium text-[#374151] mb-1.5">Descrição do Problema</label>
              <textarea value={descricaoProblema} onChange={e => setDescricaoProblema(e.target.value)} rows={3} className="w-full px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF] resize-none" />
            </div>
          )}

          {motivo === 'Outro' && (
            <div>
              <label className="block text-[13px] font-medium text-[#374151] mb-1.5">Descreva o Motivo</label>
              <textarea value={descricaoOutro} onChange={e => setDescricaoOutro(e.target.value)} rows={3} className="w-full px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF] resize-none" />
            </div>
          )}

          {anexoObrigatorio && (
            <div>
              <label className="block text-[13px] font-medium text-[#374151] mb-1.5">
                {anexoObrigatorio.titulo}
                <span className="text-[#B91C1C]"> *</span>
              </label>
              <label className={`flex items-center gap-3 px-3 py-3 rounded-lg border border-dashed cursor-pointer transition-colors ${erroAnexo ? 'border-[#FCA5A5] bg-[#FEF2F2]' : 'border-[#D1D5DB] hover:bg-[#F7F8FA]'}`}>
                <Upload className="w-4.5 h-4.5 text-[#1E4FBF]" strokeWidth={1.75} />
                <span className="text-[13.5px] text-[#4B5563]">
                  {atestadoFile
                    ? atestadoFile.name
                    : (editando && protocolo?.atestadoUrl)
                        ? `Arquivo atual: ${protocolo.atestadoNomeArquivo || 'anexo'} (clique para substituir)`
                        : anexoObrigatorio.dica}
                </span>
                <input
                  type="file"
                  accept=".pdf,image/*"
                  className="hidden"
                  onChange={(e) => {
                    setAtestadoFile(e.target.files?.[0] || null)
                    setErroAnexo('')
                  }}
                />
              </label>
              {erroAnexo && (
                <p className="flex items-center gap-1.5 text-[12px] text-[#B91C1C] mt-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" strokeWidth={2} />
                  {erroAnexo}
                </p>
              )}
              <p className="text-[12px] text-[#9CA3AF] mt-1.5">
                O anexo é obrigatório para este motivo e ficará disponível para o responsável pela análise.
              </p>
              {atestadoUploading && <p className="text-[12px] text-[#1E4FBF] mt-1">Enviando arquivo...</p>}
            </div>
          )}

          {motivo === 'Declaração de Comparecimento' && (
            <div className="bg-[#FFF7ED] border border-[#FED7AA] rounded-lg px-3.5 py-3">
              <p className="text-[13px] text-[#9A3412] font-medium">Atenção: esta opção não gera abono para o ponto.</p>
              <p className="text-[12.5px] text-[#9A3412] mt-0.5">
                O período será compensado com horas ou, caso não haja compensação, será registado como falta.
              </p>
            </div>
          )}

          <div>
            <label className="block text-[13px] font-medium text-[#374151] mb-1.5">Outros Anexos (opcional)</label>
            <label className="flex items-center gap-3 px-3 py-3 rounded-lg border border-dashed border-[#D1D5DB] cursor-pointer hover:bg-[#F7F8FA] transition-colors">
              <Paperclip className="w-4.5 h-4.5 text-[#1E4FBF]" strokeWidth={1.75} />
              <span className="text-[13.5px] text-[#4B5563]">
                Clique para selecionar um ou mais documentos de apoio
              </span>
              <input
                type="file"
                multiple
                className="hidden"
                onChange={(e) => {
                  const novos = Array.from(e.target.files || [])
                  setAnexosFiles(prev => [...prev, ...novos])
                  e.target.value = ''
                }}
              />
            </label>
            {anexosExistentes.length > 0 && (
              <ul className="mt-2 flex flex-col gap-1.5">
                {anexosExistentes.map((a, idx) => (
                  <li key={`${a.url}-${idx}`} className="flex items-center justify-between px-3 py-2 rounded-lg bg-[#EEF3FE] text-[13px] text-[#1E4FBF]">
                    <a href={a.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 truncate">
                      <Eye className="w-3.5 h-3.5 shrink-0" strokeWidth={1.9} />
                      <span className="truncate">{a.nomeArquivo}</span>
                    </a>
                    <button type="button" onClick={() => setAnexosExistentes(prev => prev.filter((_, i) => i !== idx))} className="text-[#9CA3AF] hover:text-[#B91C1C] shrink-0 ml-2">
                      <X className="w-3.5 h-3.5" strokeWidth={2} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {anexosFiles.length > 0 && (
              <ul className="mt-2 flex flex-col gap-1.5">
                {anexosFiles.map((f, idx) => (
                  <li key={`${f.name}-${idx}`} className="flex items-center justify-between px-3 py-2 rounded-lg bg-[#F7F8FA] text-[13px] text-[#374151]">
                    <span className="truncate">{f.name}</span>
                    <button type="button" onClick={() => removerAnexo(idx)} className="text-[#9CA3AF] hover:text-[#B91C1C] shrink-0 ml-2">
                      <X className="w-3.5 h-3.5" strokeWidth={2} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {anexosUploading && <p className="text-[12px] text-[#1E4FBF] mt-1">Enviando anexos...</p>}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2.5 rounded-lg text-[13.5px] font-medium text-[#4B5563] hover:bg-[#F5F6F8]">
              Cancelar
            </button>
            <button type="submit" disabled={saving} className="px-4 py-2.5 rounded-lg text-[13.5px] font-medium bg-[#1E4FBF] hover:bg-[#1A44A6] disabled:opacity-60 text-white">
              {saving ? (editando ? 'Salvando...' : 'Enviando...') : (editando ? 'Salvar alterações' : 'Enviar')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default NovoProtocoloModal
